'use client';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { PartnerButton } from './partner-button';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useEffect, useRef, useState, useCallback } from 'react';

// ─── Constants ───────────────────────────────────────────────────────────────

const ITEM_SIZE_LG = 260;
const ITEM_SIZE_SM = 220;
const ITEM_SIZE_XS = 200;
const GAP_LG = 50;
const GAP_SM = 32;
const GAP_XS = 24;
const SPEED_PX_PER_SEC = 50; // pixels per second

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getItemWidth(): number {
  if (typeof window === 'undefined') return ITEM_SIZE_LG;
  if (window.innerWidth >= 1024) return ITEM_SIZE_LG;
  if (window.innerWidth >= 640) return ITEM_SIZE_SM;
  return ITEM_SIZE_XS;
}

function getGap(): number {
  if (typeof window === 'undefined') return GAP_LG;
  if (window.innerWidth >= 1024) return GAP_LG;
  if (window.innerWidth >= 640) return GAP_SM;
  return GAP_XS;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function Partnership({
  subtitle,
  title,
  description,
  items,
}: {
  subtitle: string;
  title: string;
  description: string;
  items: TDynamicData[];
}) {
  return (
    <section
      id="potential"
      className={cn(
        'custom-section',
        'relative gap-8 bg-[#3A332C] px-0 py-20 sm:gap-10 sm:py-32 md:py-40 lg:gap-[50px] lg:py-[200px]',
      )}
    >
      <Image
        src="/images/top-biege.svg"
        alt="Bar"
        width={0}
        height={0}
        className="absolute -top-[1px] left-1/2 z-[5] h-auto w-full -translate-x-1/2"
      />

      {/* Header */}
      <div
        className={cn(
          'custom-container',
          'relative z-10 flex flex-col items-center justify-center gap-4 px-4',
        )}
      >
        <h4 className="w-full text-center text-sm font-medium text-info lg:text-base">
          {subtitle}
        </h4>
        <Heading
          title={title}
          className={{
            container: 'w-full items-center justify-center text-center',
            title: 'text-white',
          }}
        />
        <Paragraph className="w-full text-center" color="white">
          {description}
        </Paragraph>
      </div>

      {/* Carousel */}
      <InfiniteCarousel items={items} />

      {/* CTA */}
      <div className="relative z-10 flex w-full items-center justify-center px-4">
        <PartnerButton />
      </div>

      <Image
        src="/images/bottom-beige.svg"
        alt="Bar"
        width={0}
        height={0}
        className="absolute -bottom-[1px] left-1/2 z-[5] h-auto w-full -translate-x-1/2"
      />
    </section>
  );
}

// ─── Infinite Carousel ────────────────────────────────────────────────────────

function InfiniteCarousel({ items }: { items: TDynamicData[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0); // sub-pixel accumulator
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const isPausedRef = useRef(false);

  // Triple the items so we always have a full set on both sides for seamless looping
  const tripled = [...items, ...items, ...items];

  const [activeIndex, setActiveIndex] = useState(0);

  const getStride = useCallback(() => getItemWidth() + getGap(), []);

  const getTileWidth = useCallback(() => items.length * getStride(), [items.length, getStride]);

  // Snap offset into [0, tileWidth) so the loop is invisible
  const normalise = useCallback(
    (offset: number) => {
      const tile = getTileWidth();
      return ((offset % tile) + tile) % tile;
    },
    [getTileWidth],
  );

  // Determine active index using a ±20% viewport activation zone.
  // Hysteresis: only switch when the closest in-zone item differs from current,
  // preventing rapid flicker at zone boundaries.
  const computeActive = useCallback(() => {
    if (!trackRef.current) return;

    const stride = getStride();
    const tile = getTileWidth();
    const vw = window.innerWidth;
    const viewportCenter = vw / 2;
    const zone = vw * 0.2; // 20% of viewport on each side

    const baseOffset = tile - offsetRef.current;

    // Collect every item whose center falls within the activation zone
    const inZone: { idx: number; dist: number }[] = [];

    for (let i = 0; i < tripled.length; i++) {
      const itemCenter = -baseOffset + i * stride + getItemWidth() / 2;
      const dist = Math.abs(itemCenter - viewportCenter);
      if (dist <= zone) {
        inZone.push({ idx: i % items.length, dist });
      }
    }

    if (inZone.length === 0) return; // nothing in zone — keep current active

    // Closest to dead-center within the zone wins
    inZone.sort((a, b) => a.dist - b.dist);
    const next = inZone[0].idx;

    setActiveIndex(prev => (prev !== next ? next : prev));
  }, [getStride, getTileWidth, tripled.length, items.length]);

  // Apply CSS transform to the track
  const applyTransform = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const tile = getTileWidth();
    // Offset the middle copy into view initially
    track.style.transform = `translateX(${-(tile - offsetRef.current)}px)`;
  }, [getTileWidth]);

  const tick = useCallback(
    (timestamp: number) => {
      if (lastTimeRef.current === null) lastTimeRef.current = timestamp;
      const delta = (timestamp - lastTimeRef.current) / 1000; // seconds
      lastTimeRef.current = timestamp;

      if (!isPausedRef.current) {
        offsetRef.current = normalise(offsetRef.current + SPEED_PX_PER_SEC * delta);
        applyTransform();
        computeActive();
      }

      rafRef.current = requestAnimationFrame(tick);
    },
    [normalise, applyTransform, computeActive],
  );

  useEffect(() => {
    // Seed offset so the center item starts centered
    const stride = getStride();
    const tile = getTileWidth();
    const viewportCenter = window.innerWidth / 2;
    const centerItemOffset = Math.floor(items.length / 2) * stride + getItemWidth() / 2;
    offsetRef.current = normalise(tile - viewportCenter + centerItemOffset);
    applyTransform();
    computeActive();

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="relative w-full overflow-hidden"
      onMouseEnter={() => {
        isPausedRef.current = true;
      }}
      onMouseLeave={() => {
        isPausedRef.current = false;
        lastTimeRef.current = null; // reset delta to avoid jump
      }}
    >
      {/* Fade edges */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-[#3A332C] to-transparent sm:w-32 lg:w-48"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-[#3A332C] to-transparent sm:w-32 lg:w-48"
      />

      {/* Track — will-change lets the browser promote to its own layer */}
      <div
        ref={trackRef}
        className="flex items-center py-6 will-change-transform"
        style={{ gap: `${GAP_XS}px` }}
      >
        {tripled.map((item, i) => (
          <ItemCircle key={i} item={item} isActive={i % items.length === activeIndex} />
        ))}
      </div>
    </div>
  );
}

// ─── Item Circle ──────────────────────────────────────────────────────────────

function ItemCircle({ item, isActive }: { item: TDynamicData; isActive?: boolean }) {
  return (
    <div
      className={cn(
        // Base — fixed size, no flex-shrink so the track width is predictable
        'relative flex aspect-square shrink-0 items-center justify-center text-balance rounded-full border border-white text-center text-sm font-medium tracking-tight text-white',
        'w-[200px] sm:w-[220px] lg:min-w-[260px] lg:max-w-[260px]',
        'sm:text-base lg:text-lg',
        // Transition only opacity/scale — transform is driven by the RAF loop
        'transition-[opacity,scale] duration-500 ease-in-out',
        isActive ? 'ea-item-circle__active scale-100 opacity-100' : 'scale-90 opacity-50',
      )}
    >
      <p className="w-full px-[18px] sm:px-20 lg:px-8">{item.name}</p>
    </div>
  );
}
